import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

export const GET_CONTRIBUTOR_PROFILE_PHOTO_URL_QUERY_KEY = 'contributor-profile-photo-url';

/**
 * Hook to get contributor profile photo URL.
 * Fetches the redirect URL with authentication, then returns the final download URL
 * (which includes the SAS token and can be used directly in img src).
 */
export const useGetContributorProfilePhotoUrl = (contributorId: string | undefined, assetName: string | undefined) => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [GET_CONTRIBUTOR_PROFILE_PHOTO_URL_QUERY_KEY, contributorId, assetName],
    queryFn: async () => {
      if (!contributorId || !assetName) {
        return null;
      }

      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/contributors/${contributorId}/profile-photo`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token || ''}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Contributor profile photo not found');
        }
        throw new Error(`Failed to get profile photo URL: ${response.status} ${response.statusText}`);
      }

      // Parse JSON response with url field
      const data = await response.json();
      if (!data?.url) {
        throw new Error('No URL found in response');
      }

      return data.url;
    },
    enabled: !!contributorId && !!assetName,
    staleTime: 23 * 60 * 60 * 1000, // 23 hours (just under 24h expiration)
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};
